import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { InventoryImportMode, InventoryImportResult, InventoryImportRow } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Modal } from '../components/Overlay';
import { useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { buildCountTemplate, download, parseInventoryWorkbook, readFileBytes } from '../lib/inventoryExcel';
import { formatNumber } from '../lib/format';
import type { Language } from '../i18n';

const SCOPE = 'importar:inventario';

/** Importación de inventario desde Excel: vista previa con todos los errores y aplicación todo o nada. */
export function InventoryImportModal({ open, defaultMode, onClose, onImported }: { open: boolean; defaultMode: InventoryImportMode; onClose: () => void; onImported: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const places = user?.locations ?? [];
  const [mode, setMode] = useState<InventoryImportMode>(defaultMode);
  const [location, setLocation] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [full, setFull] = useState(false);
  const [file, setFile] = useState<{ name: string; rows: InventoryImportRow[]; hasLocation: boolean; skipped: string[] } | null>(null);
  const [preview, setPreview] = useState<InventoryImportResult | null>(null);
  const locationId = location ?? places[0]?.id ?? null;

  const body = (rows: InventoryImportRow[], source: string) => ({ mode, location_id: locationId, rows, source, reason, full: mode === 'replace' && full });

  const runPreview = async (rows: InventoryImportRow[], source: string, m: InventoryImportMode = mode, loc: number | null = locationId) => {
    try {
      setPreview(await api.importInventoryPreview({ ...body(rows, source), mode: m, location_id: loc }));
    } catch (e) {
      setPreview(null);
      showError(e);
    }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const parsed = parseInventoryWorkbook(await readFileBytes(f));
    setPreview(null);
    setFile({ name: f.name, rows: parsed.rows, hasLocation: parsed.hasLocation, skipped: parsed.skipped });
    releaseKey(SCOPE);
    if (parsed.rows.length === 0) return show(t('invImport.noRows'));
    await runPreview(parsed.rows, f.name);
  };

  const changeMode = (m: InventoryImportMode) => {
    setMode(m);
    if (file && file.rows.length > 0) void runPreview(file.rows, file.name, m);
  };

  const changeLocation = (id: number) => {
    setLocation(id);
    if (file && file.rows.length > 0) void runPreview(file.rows, file.name, mode, id);
  };

  const commit = () =>
    run(async () => {
      if (!file || !preview) return;
      try {
        const r = await api.importInventoryCommit(requestKey(SCOPE), body(file.rows, file.name));
        releaseKey(SCOPE);
        show(t(`invImport.done.${mode}`, { docs: r.documents.map((d) => d.doc_no).join(', ') }));
        setFile(null);
        setPreview(null);
        onImported();
        onClose();
      } catch (e) {
        showError(e);
      }
    });

  const errors = preview?.rows.filter((r) => r.status === 'error') ?? [];
  const okRows = preview?.rows.filter((r) => r.status === 'ok').length ?? 0;
  const needsReason = mode === 'replace' && !reason.trim();

  return (
    <Modal open={open} title={t('invImport.title')} help={t('invImport.help')} onClose={onClose}>
      <div className="formgrid" id="invImportForm">
        <div className="field">
          <label htmlFor="invImportMode">{t('invImport.mode')}</label>
          <select id="invImportMode" value={mode} onChange={(e) => changeMode(e.target.value as InventoryImportMode)}>
            <option value="audit">{t('invImport.modes.audit')}</option>
            <option value="sum">{t('invImport.modes.sum')}</option>
            {can('inventory.adjust') ? <option value="replace">{t('invImport.modes.replace')}</option> : null}
          </select>
          <div className="help">{t(`invImport.modeHelp.${mode}`)}</div>
        </div>
        <div className="field">
          <label htmlFor="invImportLocation">{t('invImport.location')}</label>
          <select id="invImportLocation" value={locationId ?? ''} disabled={file?.hasLocation} onChange={(e) => changeLocation(Number(e.target.value))}>
            {places.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <div className="help">{t(file?.hasLocation ? 'invImport.locationFromFile' : 'invImport.locationHelp')}</div>
        </div>
        <div className="filezone span2">
          <b>{t('invImport.columns')}</b>
          <br />
          <span className="tiny muted">{t('invImport.columnsHelp')}</span>
          <input id="invImportFile" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => void onFile(e)} />
          <button type="button" className="btn sm" id="invImportTemplate" onClick={() => download(buildCountTemplate(), 'Conteo_locales_TEO.xlsx')}>
            {t('invImport.template')}
          </button>
        </div>
        {file ? (
          <div className="v52-note span2" id="invImportSummary">
            {t('invImport.fileSummary', { name: file.name, n: file.rows.length })}
            {file.skipped.length > 0 ? ` ${t('invImport.skippedSheets', { sheets: file.skipped.join(', ') })}` : ''}
          </div>
        ) : null}

        {preview ? (
          <>
            <div className="kpis span2" id="invImportPreview">
              <div className="kpi">
                <div className="lab">{t('invImport.kpiOk')}</div>
                <div className="val">{formatNumber(okRows, lang)}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('invImport.kpiUnits')}</div>
                <div className="val">{formatNumber(preview.locations.reduce((s, l) => s + l.units, 0), lang)}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('invImport.kpiSkipped')}</div>
                <div className="val">{formatNumber(preview.skipped, lang)}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('invImport.kpiErrors')}</div>
                <div className="val">{formatNumber(preview.errors, lang)}</div>
              </div>
            </div>
            <div className="span2 tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('invImport.cols.location')}</th>
                    <th>{t('invImport.cols.lines')}</th>
                    <th>{t('invImport.cols.units')}</th>
                    <th>{t('invImport.cols.system')}</th>
                    <th>{t('invImport.cols.difference')}</th>
                  </tr>
                </thead>
                <tbody id="invImportLocations">
                  {preview.locations.map((l) => (
                    <tr key={l.location_id} data-location={l.location_code}>
                      <td>
                        <b>{l.location_code}</b>
                      </td>
                      <td>{formatNumber(l.lines, lang)}</td>
                      <td>{formatNumber(l.units, lang)}</td>
                      <td>{formatNumber(l.system_units, lang)}</td>
                      <td>{mode === 'sum' ? `+${formatNumber(l.units, lang)}` : formatNumber(l.units - l.system_units, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.length > 0 ? (
              <div className="span2 tablewrap v52-modal-table" id="invImportErrors">
                <table>
                  <thead>
                    <tr>
                      <th>{t('invImport.cols.row')}</th>
                      <th>{t('invImport.cols.location')}</th>
                      <th>{t('invImport.cols.ref')}</th>
                      <th>{t('invImport.cols.variant')}</th>
                      <th>{t('invImport.cols.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.slice(0, 200).map((r, i) => (
                      <tr key={`${r.row}-${i}`}>
                        <td>{r.row}</td>
                        <td>{r.location_code}</td>
                        <td>{r.ref}</td>
                        <td>{[r.color, r.size].filter(Boolean).join(' / ')}</td>
                        <td>{t(`errors.${r.reason}`, { defaultValue: r.reason ?? '' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {mode === 'replace' ? (
              <>
                <div className="field span2">
                  <label htmlFor="invImportReason">{t('invImport.reason')}</label>
                  <input id="invImportReason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                <label className="check span2">
                  <input type="checkbox" id="invImportFull" checked={full} onChange={(e) => setFull(e.target.checked)} />
                  {t('invImport.full')}
                </label>
              </>
            ) : null}
            <div className="help span2">{preview.errors > 0 ? t('invImport.blocked') : t('invImport.ready')}</div>
            <button type="button" className="btn black span2" id="invImportCommit" disabled={busy || preview.errors > 0 || okRows === 0 || needsReason} onClick={() => void commit()}>
              {t(`invImport.commit.${mode}`)}
            </button>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
